import React, { FC, useMemo, useState } from 'react';

import { Alert, Collapse, DatePicker, Segmented, Spin, Switch, Tabs, Tooltip } from 'antd';
import moment, { Moment } from 'moment';

import CohortsTab from './components/CohortsTab';
import KpiTiles from './components/KpiTiles';
import OverviewTab from './components/OverviewTab';
import SchoolsTab from './components/SchoolsTab';
import SummaryTable from './components/SummaryTable';
import { FIRST_DAY, GRANULARITY_OPTIONS } from './constants';
import styles from './CrmAnalytics.module.scss';
import { useCrmAnalytics } from './useCrmAnalytics';
import { TCrmGranularity, TCrmPeriod, TCrmSignal } from '../../../shared/common/api/endpoints/crmAnalyticsApi';
import MainLayout from '../../../shared/common/components/layouts/MainLayout';
import PageHead from '../../../shared/common/components/page-header/PageHeader';

type TRange = [ Moment, Moment ];

const DATE_FORMAT = 'YYYY-MM-DD';

const defaultRange = (): TRange => [ moment().startOf('month').subtract(11, 'months'), moment() ];

const RANGE_PRESETS: Record<string, TRange> = {
  '30 дней': [ moment().subtract(29, 'days'), moment() ],
  'Этот месяц': [ moment().startOf('month'), moment() ],
  'Этот год': [ moment().startOf('year'), moment() ],
  '12 месяцев': defaultRange(),
  'Всё время': [ moment(FIRST_DAY), moment() ],
};

// CRM → Аналитика. Клиенты сервиса: статусы школ, доход, конверсия из теста, срок жизни и отток.
// Определения согласованы 28.10.2026, расчёт — бэк CrmAnalytics (analytics-backend).
const CrmAnalytics: FC = () => {
  const [ range, setRange ] = useState<TRange>(defaultRange);
  const [ granularity, setGranularity ] = useState<TCrmGranularity>('month');
  const [ onlyReal, setOnlyReal ] = useState(true);
  const [ activeTab, setActiveTab ] = useState('overview');
  const [ schoolSignals, setSchoolSignals ] = useState<TCrmSignal[]>([]);

  const period = useMemo<TCrmPeriod>(() => ({
    date_from: range[0].format(DATE_FORMAT),
    date_to: range[1].format(DATE_FORMAT),
    granularity,
  }), [ range, granularity ]);

  const {
    summary,
    cohorts,
    schools,
    periodLoading,
    schoolsLoading,
    periodError,
    schoolsError,
  } = useCrmAnalytics(period);

  const openSchools = (signal: TCrmSignal) => {
    setSchoolSignals([ signal ]);
    setActiveTab('schools');
  };

  const tabs = [
    {
      key: 'overview',
      label: 'Обзор',
      children: <OverviewTab summary={summary} onlyReal={onlyReal} />,
    },
    {
      key: 'table',
      label: 'Управленческая таблица',
      children: <SummaryTable summary={summary} onlyReal={onlyReal} />,
    },
    {
      key: 'cohorts',
      label: 'Конверсия из теста',
      children: <CohortsTab cohorts={cohorts} />,
    },
    {
      key: 'schools',
      label: 'Школы',
      children: (
        <SchoolsTab
          schools={schools}
          loading={schoolsLoading}
          error={schoolsError}
          signals={schoolSignals}
          onSignalsChange={setSchoolSignals}
        />
      ),
    },
  ];

  return (
    <MainLayout pageTitle="Аналитика клиентов">
      <div className="page-wrapper">
        <PageHead title="Аналитика клиентов" />

        <div className="page-content">
          <div className={styles.toolbar}>
            <DatePicker.RangePicker
              value={range}
              allowClear={false}
              format="DD.MM.YYYY"
              ranges={RANGE_PRESETS}
              disabledDate={(date) => date.isAfter(moment(), 'day')}
              onChange={(values) => {
                if (values && values[0] && values[1]) setRange([ values[0], values[1] ]);
              }}
            />
            <Segmented
              value={granularity}
              onChange={(value) => setGranularity(value as TCrmGranularity)}
              options={GRANULARITY_OPTIONS}
            />
            <Tooltip title="Статус «Получен», основной счёт, без бонусных пополнений. Выключите, чтобы увидеть бонусы, «Не учитывать» и возвраты">
              <span className={styles.toolbarItem}>
                <Switch checked={onlyReal} onChange={setOnlyReal} size="small" />
                Только реальные платежи
              </span>
            </Tooltip>
            {periodLoading && <Spin size="small" />}
          </div>

          {periodError && <Alert type="error" showIcon message={periodError} style={{ marginBottom: 16 }} />}

          <KpiTiles
            summary={summary}
            cohorts={cohorts}
            schools={schools}
            onlyReal={onlyReal}
            onOpenSchools={openSchools}
          />

          <Collapse ghost className={styles.method}>
            <Collapse.Panel header="Как считается" key="method">
              <ul>
                <li><b>Статус школы за день</b> — по ночной детализации биллинга, одна строка на школу за день. Платят — было списание за доступ; льготные — выручка школы за 30 дней меньше 100 тыс. ₽, доступ бесплатный; неживые льготные — выручки нет совсем; приостановлены — выручка выше порога, денег на балансе нет.</li>
                <li><b>Реальный платёж</b> — статус «Получен», основной счёт, не бонусное пополнение. Комиссия партнёров показана отдельно и из выручки не вычитается.</li>
                <li><b>Поступления и начислено.</b> Поступления — деньги по дате платежа. Начислено — списания за дни доступа: сколько сервис отработал. При оплате на полгода или год они сильно расходятся.</li>
                <li><b>Покупатель</b> — школа с первым реальным платежом, в любой момент после подключения.</li>
                <li><b>Ушла</b> — платила и 30 дней подряд осталась без оплаченного доступа; дата ухода — первый день без доступа. Снова заплатила — «вернулась». Поэтому за последние 30 дней ушедшие ещё досчитываются.</li>
                <li><b>Срок жизни.</b> Платящая жизнь — дни с оплаченным доступом. В сервисе — от подключения до последнего дня с доступом.</li>
              </ul>
            </Collapse.Panel>
          </Collapse>

          <Tabs type="card" activeKey={activeTab} onChange={setActiveTab} items={tabs} />
        </div>
      </div>
    </MainLayout>
  );
};

export default CrmAnalytics;
